import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { Worker } from '@react-pdf-viewer/core';
import { Space, Typography, Image } from 'antd';
import { useLocation } from 'react-router-dom';

// Types
export interface FileViewerContextType {
  fileUrl: string | null;
  setFileUrl: (url: string | null) => void;
  fileType: string | null;
  setFileType: (type: string | null) => void;
}

// Context
export const FileViewerContext = createContext<FileViewerContextType | undefined>(undefined);

// Hook to use file viewer context
export const useFileViewer = (): FileViewerContextType => {
  const context = useContext(FileViewerContext);
  if (!context) {
    throw new Error('useFileViewer must be used within a FileViewerProvider');
  }
  return context;
};

// Provider component
interface FileViewerProviderProps {
  children: ReactNode;
}

export const FileViewerProvider: React.FC<FileViewerProviderProps> = ({ children }) => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fileType, setFileType] = useState<string | null>(null);
  const location = useLocation();

  // Clear file viewer state when route changes
  useEffect(() => {
    setFileUrl(null);
    setFileType(null);
  }, [location.pathname]);

  const contextValue: FileViewerContextType = {
    fileUrl,
    setFileUrl,
    fileType,
    setFileType,
  };

  return (
    <FileViewerContext.Provider value={contextValue}>
      <Worker workerUrl='/static/webpack/js/pdf.worker.min.js'>
        {children}
      </Worker>
    </FileViewerContext.Provider>
  );
};

// Utility function to detect file type from URL
export const getFileTypeFromUrl = (url: string): string => {
  if (!url) return 'unknown';

  const extension = url.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'pdf':
      return 'pdf';
    case 'xml':
      return 'xml';
    default:
      return 'unknown';
  }
};

// Mapping function from MIME types to viewer types
export const mapMimeTypeToViewer = (mimeType: string): string => {
  if (!mimeType) return 'unknown';

  const normalizedMime = mimeType.toLowerCase().trim();

  // Map standard MIME types to viewer types
  switch (normalizedMime) {
    case 'application/pdf':
      return 'pdf';

    case 'application/xml':
    case 'text/xml':
    case 'application/xhtml+xml':
      return 'xml';

    // Add other MIME type mappings as needed
    case 'text/plain':
    case 'text/csv':
    case 'application/json':
      return 'text';

    default:
      return 'unknown';
  }
};

// Loading component
export const FileLoadingState: React.FC<{ percentage?: number }> = ({ percentage }) => (
  <div style={{ width: "100%", textAlign: "center", padding: "2rem" }}>
    <Space direction='vertical' size="large">
      <Image width={200} src="/static/icons/tso-badge.svg" preview={false} />
      {percentage !== undefined && (
        <Typography.Text>Loading... {percentage.toFixed(1)}%</Typography.Text>
      )}
    </Space>
  </div>
);

// Empty state component
export const FileEmptyState: React.FC<{ message?: string }> = ({
  message = "No file selected"
}) => (
  <div style={{ width: "100%", textAlign: "center", padding: "2rem" }}>
    <Space direction='vertical' size="large">
      <Image width={200} src="/static/icons/tso-badge.svg" preview={false} />
      <Typography.Text type="secondary">{message}</Typography.Text>
    </Space>
  </div>
);

// Error state component
export const FileErrorState: React.FC<{ error: string; fileName?: string }> = ({
  error,
  fileName
}) => (
  <div style={{ width: "100%", textAlign: "center", padding: "2rem" }}>
    <Space direction='vertical' size="large">
      <Image width={200} src="/static/icons/tso-badge.svg" preview={false} />
      <Typography.Title level={4} type="danger">
        {fileName ? `Error loading ${fileName}` : 'File Error'}
      </Typography.Title>
      <Typography.Text type="secondary">{error}</Typography.Text>
    </Space>
  </div>
);
